import { DestroyRef, inject, Injector } from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withLinkedState,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import type { OnyxOptional } from '@onyx/angular';
import { OnyxFilterPipe, OnyxNamePipe } from '@onyx/angular';
import { cloneDeep, isEqual } from 'lodash';
import { DateTime } from 'luxon';
import type { Observable } from 'rxjs';
import { map, merge, startWith, switchMap, takeUntil, tap } from 'rxjs';
import { AuthStore } from '../../auth/common/stores/auth.store';
import { TechnicalPointCategory } from '../../dashboard/fleet/common/enums/technical-point-category';
import { FleetTypeHelper } from '../../dashboard/fleet/common/helpers/fleet-type.helper';
import type { SwapPoint } from '../../dashboard/fleet/common/interfaces/technical-point';
import { EmployeeRole } from '../../dashboard/management-panel/employees/common/enums/employee-role';
import { MapStore } from '../../dashboard/map/common/stores/map.store';
import { SwapStore } from '../../dashboard/map/common/stores/swap.store';
import { TelematicsCargoFilter } from '../enums/telematics/telematics-cargo-filter';
import { TelematicsCrewFilter } from '../enums/telematics/telematics-crew-filter';
import { TelematicsDelegationFilter } from '../enums/telematics/telematics-delegation-filter';
import { TelematicsSort } from '../enums/telematics/telematics-sort';
import { UserSettingsScope } from '../enums/user-settings-scope';
import { RouteHelper } from '../helpers/route.helper';
import type { TelematicsFilters } from '../interfaces/telematics/telematics-filters';
import type { TelematicsItem } from '../interfaces/telematics/telematics-item';
import type { TelematicsTechnicalRoutePoint } from '../interfaces/telematics/telematics-route-point';
import type { TelematicsTemplate } from '../interfaces/telematics/telematics-template';
import { TelematicsIdentifierPipe } from '../pipes/telematics-fleet-identifier.pipe';
import { TelematicsDataService } from '../services/telematics-data.service';
import { TelematicsService } from '../services/telematics.service';
import { UserSettingsService } from '../services/user-settings.service';

interface TelematicsState {
  readonly DATA_START: DateTime;

  query: string;
  sort: TelematicsSort;
  filters: TelematicsFilters;
  showFilters: boolean;

  loading: boolean;
  unsortedData: Map<string, TelematicsItem>;

  defaultFilters: TelematicsFilters;
}

const TEMPLATES_SCOPE = UserSettingsScope.TELEMATICS_FILTERS;

export const TelematicsStore = signalStore(
  { providedIn: 'root' },
  withState<TelematicsState>(() => getInitialState(inject(AuthStore))),
  withProps(() => ({
    _authStore: inject(AuthStore),
    _filterPipe: inject(OnyxFilterPipe),
    _namePipe: inject(OnyxNamePipe),
    _telematicsDataService: inject(TelematicsDataService),
    _telematicsIdentifierPipe: inject(TelematicsIdentifierPipe),
    _userSettingsService: inject(UserSettingsService),
    _telematicsService: inject(TelematicsService),
    _destroyRef: inject(DestroyRef),
    _injector: inject(Injector),
  })),
  withProps((store) => ({
    loading$: toObservable(store.loading),

    templates: toSignal(
      merge(store._userSettingsService.reload$, store._authStore.login$).pipe(
        startWith(null),
        switchMap(() =>
          store._userSettingsService.getSettings(TEMPLATES_SCOPE),
        ),
        map(({ settings, selected }): TelematicsTemplate[] => [
          {
            uuid: 'default',
            name: 'labels.default',
            value: cloneDeep(store.defaultFilters()),
            default: true,
          },
          ...settings.map((setting) => ({
            uuid: setting.uuid,
            name: setting.name,
            value: JSON.parse(setting.value),
            active: setting.uuid === selected,
          })),
        ]),
      ),
      { initialValue: null },
    ),
  })),
  withComputed((store) => ({
    hasFilters: () => !isEqual(store.filters(), store.defaultFilters()),
  })),
  withLinkedState((store) => ({
    activeTemplate: () =>
      store
        .templates()
        ?.find((template) => template.active && template.uuid !== 'default') ??
      null,
  })),
  withComputed((store) => ({
    hasTemplateChanges: () =>
      !isEqual(store.activeTemplate()?.value, store.filters()),

    _templateFilters: () =>
      store.activeTemplate()?.value ?? cloneDeep(store.defaultFilters()),
  })),
  withMethods((store) => ({
    setQuery(query: string): void {
      patchState(store, { query });
    },

    setSort(sort: TelematicsSort): void {
      patchState(store, { sort });
    },

    setFilters(filters: TelematicsFilters): void {
      patchState(store, { filters });
    },

    updateFilters(filters: Partial<TelematicsFilters>): void {
      patchState(store, (state) => ({
        filters: { ...state.filters, ...filters },
      }));
    },

    clearFilters(): void {
      patchState(store, () => ({
        sort: TelematicsSort.ORDER_DELAY_DESC,
        filters: store._templateFilters(),
      }));
    },

    toggleShowFilters(force?: boolean): void {
      patchState(store, (state) => ({
        showFilters: force ?? !state.showFilters,
      }));
    },

    changeTemplate(uuid: string): void {
      const template = store
        .templates()
        ?.find((template) => template.uuid === uuid);
      if (!template) return;

      const activeTemplate = template.uuid === 'default' ? null : template;
      patchState(store, {
        activeTemplate,
        ...(activeTemplate?.value && { filters: activeTemplate.value }),
      });
      if (template.uuid === 'default') this.clearFilters();

      store._userSettingsService
        .selectSetting(TEMPLATES_SCOPE, uuid)
        .subscribe();
    },

    addTemplate(name: string): Observable<void> {
      return store._userSettingsService
        .addSetting(TEMPLATES_SCOPE, {
          name,
          value: JSON.stringify(store.filters()),
        })
        .pipe(
          tap(() => this.toggleShowFilters(false)),
          map((template) => this.changeTemplate(template.uuid)),
        );
    },

    editTemplate(uuid: string, name: string): Observable<void> {
      return store._userSettingsService
        .editSetting(TEMPLATES_SCOPE, uuid, {
          name,
          value: JSON.stringify(store.filters()),
        })
        .pipe(
          tap(() => this.toggleShowFilters(false)),
          map(() => undefined),
        );
    },

    deleteTemplate(uuid: string): Observable<void> {
      return store._userSettingsService.deleteSetting(TEMPLATES_SCOPE, uuid);
    },
  })),
  withComputed((store) => ({
    unfilteredData: () =>
      store._telematicsService.getSortedData(
        [...store.unsortedData().values()],
        store.sort(),
      ),
  })),
  withComputed((store) => ({
    data: (): TelematicsItem[] => {
      const swapStore = store._injector.get(SwapStore);
      if (swapStore.showSwapPanel()) {
        return store
          .unfilteredData()
          .filter((item) => FleetTypeHelper.isVehicle(item.generalInformation));
      }

      const mapStore = store._injector.get(MapStore);
      const filteredData = store._telematicsService.getFilteredData(
        store.unfilteredData(),
        store.filters(),
        store.query(),
        store._authStore.isSystemAdmin(),
        mapStore.selectedVehicle()?.uuid,
      );

      return filteredData;
    },
    swapPointsBySwapUuid: () => {
      const result = new Map<string, SwapPoint[]>();

      for (const item of store.unfilteredData()) {
        for (const point of item.routePoints) {
          const technicalPoint = point.technicalPoint;
          if (
            !technicalPoint ||
            technicalPoint.category !== TechnicalPointCategory.SWAP_POINT
          ) {
            continue;
          }

          const swapUuid = technicalPoint.swapData.uuid;
          const swap = result.get(swapUuid);
          if (!swap) {
            result.set(swapUuid, [technicalPoint]);
          } else {
            swap.push(technicalPoint);
          }
        }
      }

      return result;
    },
    swapPointBySwapAndVehicleUuid: () => {
      const result = new Map<
        string,
        Map<string, TelematicsTechnicalRoutePoint>
      >();

      for (const item of store.unfilteredData()) {
        for (const point of item.routePoints) {
          const technicalPoint = point.technicalPoint;
          if (
            !technicalPoint ||
            technicalPoint.category !== TechnicalPointCategory.SWAP_POINT
          ) {
            continue;
          }

          const swapUuid = technicalPoint.swapData.uuid;
          if (!result.has(swapUuid)) {
            result.set(
              swapUuid,
              new Map<string, TelematicsTechnicalRoutePoint>(),
            );
          }

          const technicalPointsMap = result.get(swapUuid)!;
          technicalPointsMap.set(item.uuid, point);

          if (item.assignedTrailer?.uuid) {
            technicalPointsMap.set(item.assignedTrailer.uuid, point);
          }
        }
      }

      return result;
    },
    telematicsItemsBySwapUuid: () => {
      const result = new Map<string, TelematicsItem[]>();

      for (const item of store.unfilteredData()) {
        for (const point of item.routePoints) {
          const technicalPoint = point.technicalPoint;
          if (
            !technicalPoint ||
            technicalPoint.category !== TechnicalPointCategory.SWAP_POINT
          ) {
            continue;
          }

          const swapUuid = technicalPoint.swapData.uuid;
          const items = result.get(swapUuid);
          if (!items) {
            result.set(swapUuid, [item]);
          } else {
            items.push(item);
          }
        }
      }

      return result;
    },
    cycleIndexByPointUuid: () => {
      const result = new Map<string, number>();

      for (const item of store.unfilteredData()) {
        for (const point of item.routePoints) {
          result.set(RouteHelper.getUuid(point), point.cycleIndex);
        }
      }

      return result;
    },
  })),
  withMethods((store) => ({
    getItem(uuid: OnyxOptional<string>): TelematicsItem | null {
      return uuid ? (store.unsortedData().get(uuid) ?? null) : null;
    },
  })),
  withHooks((store) => ({
    onInit(): void {
      store._authStore.login$
        .pipe(
          startWith(undefined),
          tap(() =>
            patchState(
              store,
              getInitialState(store._authStore, store._templateFilters()),
            ),
          ),
          switchMap(() =>
            store._telematicsDataService
              .getData$()
              .pipe(takeUntil(store._authStore.logout$)),
          ),
          takeUntilDestroyed(store._destroyRef),
        )
        .subscribe((data) =>
          patchState(store, {
            loading: data == null,
            unsortedData: data ?? new Map(),
          }),
        );

      store._authStore.logout$
        .pipe(takeUntilDestroyed(store._destroyRef))
        .subscribe(() => patchState(store, getInitialState(store._authStore)));
    },
  })),
);

export type TelematicsStore = InstanceType<typeof TelematicsStore>;

const getInitialState = (
  authStore: AuthStore,
  filters?: TelematicsFilters,
): TelematicsState => {
  const defaultFilters = getDefaultFilters(authStore);

  return {
    DATA_START: DateTime.now().minus({ days: 30 }).startOf('day'),

    query: '',
    sort: TelematicsSort.ORDER_DELAY_DESC,
    filters: filters ?? defaultFilters,
    showFilters: false,

    loading: true,
    unsortedData: new Map(),

    defaultFilters: defaultFilters,
  };
};

const getDefaultFilters = (authStore: AuthStore): TelematicsFilters => {
  const { roles, employeeUuid } = authStore.user() ?? {};

  return {
    vehicleStatus: [],
    vehicleCompliance: [],
    vehicleTachographStatus: [],
    vehicleTrailerStatus: [],

    currentAddress: null,
    currentRadius: 5_000,
    finalAddress: null,
    finalRadius: 5_000,

    employees:
      !employeeUuid ||
      roles?.some((role) =>
        [EmployeeRole.MANAGER, EmployeeRole.BUSINESS_ADMIN].includes(role),
      )
        ? []
        : [employeeUuid],

    onlyVehiclesWithProposals: false,
    planningMode: null,
    countries: [],
    route: [],

    remainingDailyDrivingTime: null,
    remainingWeeklyDrivingTime: null,
    remainingFortnightDrivingTime: null,

    delegation: TelematicsDelegationFilter.ALL,
    delegationEnd: null,

    crew: TelematicsCrewFilter.ALL,

    fuelType: [],
    remainingFuel: null,

    cargo: TelematicsCargoFilter.ALL,

    vehicleCategory: [],
    cargoSpaceType: [],
    semiTrailerSize: [],
    adrClasses: [],
    onlyLowDeck: false,
    onlyInactive: false,
  };
};
